
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import AuthGuard from '@/components/auth/AuthGuard';
import Header from '@/components/Header';
import EditListingForm from '@/components/property/EditListingForm';

const EditListing = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [listing, setListing] = useState(null);

  useEffect(() => {
    if (id && user) {
      fetchListing();
    }
  }, [id, user]);

  const fetchListing = async () => {
    if (!id || !user) return;

    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('id', id)
        .eq('owner_id', user.id)
        .single();

      if (error) {
        console.error('Error fetching listing:', error);
        toast.error('Failed to load listing');
        navigate('/my-listings');
        return;
      }

      setListing(data);
    } catch (error) {
      console.error('Exception fetching listing:', error);
      toast.error('Failed to load listing');
      navigate('/my-listings');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold mb-4">
            {t('listing_not_found', 'العقار غير موجود', 'Listing Not Found')}
          </h1>
          <button
            onClick={() => navigate('/my-listings')}
            className="bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary-hover"
          >
            {t('back_to_listings', 'العودة للعقارات', 'Back to My Listings')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <AuthGuard requireAuth={true}>
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <EditListingForm listing={listing} />
        </div>
      </div>
    </AuthGuard>
  );
};

export default EditListing;
